import Logger from "@/lib/logger.ts";

type JwtPayload = { ext?: string };

let cachedToken: string | null = null;

const isValidToken = (token: string): boolean => {
    try {
        const payload = decodeJwtPayload(token);
        if (!payload.ext) return false;

        const currentTime = Date.now() / 1000;
        const validUntil = Number(payload.ext);

        return currentTime + 60 < validUntil;
    } catch {
        return false;
    }
};

const decodeJwtPayload = (token: string): JwtPayload => {
    const [, payloadSegment] = token.split(".");
    if (!payloadSegment) {
        throw new Error("Invalid JWT format.");
    }

    const normalized = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const json = atob(padded);

    return JSON.parse(json) as JwtPayload;
};

export const getValidApiToken = async (refreshUrl: string): Promise<string | null> => {
    if (cachedToken && isValidToken(cachedToken)) {
        return cachedToken;
    }

    try {
        const response = await fetch(refreshUrl, {
            credentials: "include",
            headers: {
                "X-Requested-With": "XMLHttpRequest",
                "Cache-Control": "no-store",
            },
        });

        if (!response.ok) {
            Logger.error("Failed to fetch API token", { status: response.status });
            return null;
        }

        const data = (await response.json()) as { token?: string };
        if (!data.token || !isValidToken(data.token)) {
            Logger.error("API token response missing or invalid token");
            return null;
        }

        cachedToken = data.token;
        return cachedToken;
    } catch (err) {
        Logger.error("Failed to fetch API token", err);
        return null;
    }
};
